<template>
  <div class="dashboard-page">
    <a-row v-if="billing" :gutter="[12,12]" class="dashboard-kpis">
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card class="dashboard-kpi-card">
          <a-statistic title="总营收" :value="billing.total_revenue_cents / 100"
                        prefix="¥" :precision="2" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card class="dashboard-kpi-card">
          <a-statistic title="总成本" :value="billing.total_cost_cents / 100"
                        prefix="¥" :precision="2" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card class="dashboard-kpi-card">
          <a-statistic title="总利润" :value="billing.total_profit_cents / 100"
                        prefix="¥" :precision="2"
                        :value-style="{ color: billing.total_profit_cents >= 0 ? '#52c41a' : '#f5222d' }" />
        </a-card>
      </a-col>
      <a-col :xs="12" :sm="12" :lg="6">
        <a-card class="dashboard-kpi-card">
          <a-statistic title="节点数"
                        :value="totalProxies" />
          <div style="font-size:12px;color:#9ca3af;margin-top:4px">
            1m {{ billing.proxies_by_tier['1m'] }} ·
            3m {{ billing.proxies_by_tier['3m'] }} ·
            6m {{ billing.proxies_by_tier['6m'] }} ·
            12m {{ billing.proxies_by_tier['12m'] }}
          </div>
        </a-card>
      </a-col>
    </a-row>

    <a-row v-if="billing && billing.per_node.length" :gutter="16" style="margin-bottom:16px">
      <a-col :span="24">
        <a-card title="节点利润">
          <a-alert v-if="billing.nodes_without_cost > 0"
                    type="warning"
                    :message="`${billing.nodes_without_cost} 个节点尚未设置成本`"
                    style="margin-bottom:12px" />
          <a-table :columns="billingNodeCols" :dataSource="billing.per_node"
                   rowKey="node_id" size="small" :pagination="false" :scroll="{ x: 560 }">
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'total_cost_cents'">
                <span v-if="record.total_cost_cents !== null">¥{{ (record.total_cost_cents / 100).toFixed(2) }}</span>
                <span v-else style="color:#f5222d">未设置</span>
              </template>
              <template v-else-if="column.key === 'revenue_cents'">
                ¥{{ (record.revenue_cents / 100).toFixed(2) }}
              </template>
              <template v-else-if="column.key === 'profit_cents'">
                <span v-if="record.profit_cents !== null"
                      :style="{ color: record.profit_cents >= 0 ? '#52c41a' : '#f5222d', fontWeight: 600 }">
                  ¥{{ (record.profit_cents / 100).toFixed(2) }}
                </span>
                <span v-else style="color:#9ca3af">—</span>
              </template>
            </template>
          </a-table>
        </a-card>
      </a-col>
    </a-row>

    <a-card class="dashboard-summary-card" style="margin-bottom:16px">
      <a-row :gutter="8">
        <a-col :span="8"><a-statistic title="在线节点" :value="summary.online_nodes" /></a-col>
        <a-col :span="8"><a-statistic title="节点端口" :value="summary.total_used_ports" /></a-col>
        <a-col :span="8"><a-statistic title="总容量" :value="summary.total_capacity_ports" /></a-col>
      </a-row>
      <div class="dashboard-summary-actions">
        <a-tag color="green">● {{ summary.online_nodes }} 在线</a-tag>
        <a-tag>端口 {{ summary.total_used_ports }}/{{ summary.total_capacity_ports }} 已用</a-tag>
        <a-button type="primary" style="margin-left:8px" @click="addNodeVisible = true">+ 添加节点</a-button>
      </div>
    </a-card>

    <a-card class="dashboard-line-filter" style="margin-bottom:16px">
      <a-radio-group v-model:value="lineFilter" button-style="solid">
        <a-radio-button value="all">全部</a-radio-button>
        <a-radio-button value="网通">网通</a-radio-button>
        <a-radio-button value="电信">电信</a-radio-button>
        <a-radio-button value="双线">双线</a-radio-button>
      </a-radio-group>
    </a-card>

    <a-row :gutter="[16,16]">
      <a-col v-for="r in filteredRegions" :key="r.region_id" :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
        <RegionCard :region="r" @click="onRegionClick" />
      </a-col>
    </a-row>

    <CreateProxyModal v-model:visible="createVisible" :region="selectedRegion" @created="onCreated" />
    <AddNodeModal v-model:visible="addNodeVisible" @added="loadAll" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { getDashboard, getBilling } from '../api/dashboard';
import RegionCard from '../components/RegionCard.vue';
import CreateProxyModal from '../components/CreateProxyModal.vue';
import AddNodeModal from '../components/AddNodeModal.vue';
import type { DashboardData, BillingResp } from '../types';

const dashboard = ref<DashboardData>({ summary: { online_nodes: 0, total_used_ports: 0, total_capacity_ports: 0 }, regions: [], nodes: [] });
const lineFilter = ref('all');
const summary = computed(() => dashboard.value.summary);
const billing = ref<BillingResp | null>(null);
const totalProxies = computed(() => {
    if (!billing.value) return 0;
    const t = billing.value.proxies_by_tier;
    return (t['1m'] || 0) + (t['3m'] || 0) + (t['6m'] || 0) + (t['12m'] || 0);
});
const billingNodeCols = [
    { title: '节点', dataIndex: 'node_name', key: 'node_name', width: 200 },
    { title: '总成本', key: 'total_cost_cents', width: 120 },
    { title: '总营收', key: 'revenue_cents', width: 120 },
    { title: '利润', key: 'profit_cents', width: 120 },
];

const filteredRegions = computed(() => {
  const regions = dashboard.value.regions;
  if (lineFilter.value === 'all') return regions;
  return regions.filter((r) => r.region_name.startsWith(lineFilter.value));
});

const createVisible = ref(false);
const addNodeVisible = ref(false);
const selectedRegion = ref<any>(null);
const onRegionClick = (r: any) => { selectedRegion.value = r; createVisible.value = true; };
const onCreated = (r: any) => {
  // The modal now displays the credentials and copies them itself.
  // We just refresh the dashboard so the per-region used/capacity
  // numbers update immediately.
  loadAll();
};

const loadAll = async () => {
  try {
    const d = await getDashboard();
    dashboard.value = d.data;
  } catch { /* keep existing data */ }
  try {
    const b = await getBilling();
    billing.value = b.data;
  } catch {
    billing.value = null;
  }
};
onMounted(loadAll);
</script>

<style scoped>
.dashboard-kpis {
  margin-bottom: 16px;
}

.dashboard-summary-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.dashboard-summary-actions :deep(.ant-btn) {
  margin-left: 0 !important;
}

@media (max-width: 767px) {
  .dashboard-kpis {
    margin-bottom: 12px;
  }

  .dashboard-kpi-card {
    height: 100%;
  }

  .dashboard-kpi-card :deep(.ant-statistic-title),
  .dashboard-summary-card :deep(.ant-statistic-title) {
    font-size: 11px;
  }

  .dashboard-kpi-card :deep(.ant-statistic-content) {
    font-size: 20px;
  }

  .dashboard-summary-card :deep(.ant-statistic-content) {
    font-size: 19px;
  }

  .dashboard-summary-actions :deep(.ant-btn) {
    width: 100%;
    margin-top: 2px;
  }

  .dashboard-line-filter {
    overflow: hidden;
  }

  .dashboard-line-filter :deep(.ant-card-body) {
    overflow-x: auto;
  }

  .dashboard-line-filter :deep(.ant-radio-group) {
    display: flex;
    min-width: max-content;
  }
}
</style>
